import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Tag, Plus, Upload, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MerchantApi } from "@/api";
import {
  TermsAndConditionsSection,
  serializeTermsAndConditionsForApi,
} from "@/components/merchant/TermsAndConditionsSection";
import {
  MerchantOfferAddressDetailsSection,
  buildMerchantOfferAddressPayload,
  createEmptyMerchantOfferAddressRow,
  extractOffersOfMerchantIdFromResponse,
  type MerchantOfferAddressFormRow,
} from "@/components/merchant/MerchantOfferAddressDetailsSection";

export type OfferPageCategoryOption = {
  id: number;
  categoryCode?: string;
  categoryName?: string;
};

/** Multipart: trimmed URL string, or literal `null` when the field is empty. */
export function appendOfferUrlFormField(
  formData: FormData,
  offerUrl: string | undefined | null
): void {
  const trimmed = (offerUrl ?? "").trim();
  formData.append("offerURL", trimmed || "null");
}

const offerForOfferPageSchema = z
  .object({
    name: z
      .string()
      .min(2, { message: "Title is required" })
      .regex(/^[a-zA-Z0-9\s]+$/, {
        message: "Title cannot contain special characters",
      }),
    description: z.string().min(2, { message: "Description is required" }),
    startDate: z.string().min(1, { message: "Start date is required" }),
    endDate: z.string().min(1, { message: "End date is required" }),
    offerUrl: z.union([
      z.literal(""),
      z.string().trim().url({ message: "Please enter a valid URL" }),
    ]),
    categoryId: z.string().min(1, { message: "Select a category" }),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startDate"],
        message: "Start date cannot be after end date",
      });
    }
  });

type FormValues = z.infer<typeof offerForOfferPageSchema>;

type AddOfferForOfferPageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  categories: OfferPageCategoryOption[];
  onSuccess?: () => void;
};

export function AddOfferForOfferPageDialog({
  open,
  onOpenChange,
  merchantId,
  categories,
  onSuccess,
}: AddOfferForOfferPageDialogProps) {
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [termsLines, setTermsLines] = React.useState<string[]>([]);
  const [addressRows, setAddressRows] = React.useState<
    MerchantOfferAddressFormRow[]
  >([createEmptyMerchantOfferAddressRow()]);
  const today = new Date().toISOString().split("T")[0];

  const form = useForm<FormValues>({
    resolver: zodResolver(offerForOfferPageSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      offerUrl: "",
      categoryId: "",
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      setImageFile(null);
      setImagePreview(null);
      setTermsLines([]);
      setAddressRows([createEmptyMerchantOfferAddressRow()]);
    }
  }, [open, form]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    if (!imageFile) {
      toast.error("Please upload an offer image");
      return;
    }
    const addressPayload = buildMerchantOfferAddressPayload(addressRows);
    if (addressPayload && "error" in addressPayload) {
      toast.error(addressPayload.error);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("offerImage", imageFile);
      formData.append("merchantId", merchantId);
      formData.append("title", data.name);
      appendOfferUrlFormField(formData, data.offerUrl);
      formData.append("description", data.description);
      formData.append(
        "startDateInMillis",
        String(new Date(data.startDate).getTime())
      );
      formData.append(
        "endDateInMillis",
        String(new Date(data.endDate).getTime())
      );
      formData.append("categoryId", data.categoryId);
      formData.append(
        "termsAndConditions",
        serializeTermsAndConditionsForApi(termsLines)
      );

      const offerResponse =
        await MerchantApi.createOrUpdateMerchantOfferForOfferPage(formData);
      const offerId = extractOffersOfMerchantIdFromResponse(offerResponse);

      if (
        addressPayload &&
        "addressDetailsVOList" in addressPayload &&
        addressPayload.addressDetailsVOList.length > 0
      ) {
        if (offerId == null) {
          toast.warning(
            "Offer was saved, but addresses were not saved because the server did not return an offer id."
          );
        } else {
          try {
            await MerchantApi.createOrUpdateMerchantOfferAddressDetails({
              offersOfMerchantId: offerId,
              addressDetailsVOList: addressPayload.addressDetailsVOList,
            });
          } catch (addrErr: unknown) {
            const msg =
              addrErr &&
              typeof addrErr === "object" &&
              "message" in addrErr &&
              typeof (addrErr as { message?: unknown }).message === "string"
                ? (addrErr as { message: string }).message
                : "Offer was saved, but saving addresses failed.";
            toast.error(msg);
          }
        }
      }

      toast.success("Offer added successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.message || "Failed to add offer"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabel = (c: OfferPageCategoryOption) => {
    const name = c.categoryName || c.categoryCode || "Category";
    if (c.categoryCode && c.categoryName !== c.categoryCode) {
      return `${c.categoryName} (${c.categoryCode})`;
    }
    return name;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Add New Offer
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="space-y-2">
              <FormLabel>Image*</FormLabel>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="offer-for-offer-page-image"
                />
                <label
                  htmlFor="offer-for-offer-page-image"
                  className="cursor-pointer block"
                >
                  <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                    {imagePreview ? (
                      <div className="relative w-full h-full">
                        <img
                          src={imagePreview}
                          alt="Offer preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                          <Upload className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Plus className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">
                          Click to upload offer image
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PNG, JPG up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Summer Discount"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^a-zA-Z0-9\s]/g,
                          ""
                        );
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description*</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Offer description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="offerUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Offer URL</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://example.com/offer"
                        {...field}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard
                            .readText()
                            .then((text) => {
                              if (text.startsWith("http")) {
                                field.onChange(text);
                                toast.info("URL pasted");
                              }
                            })
                            .catch(() => {
                              toast.error("Could not read from clipboard");
                            });
                        }}
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date*</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date*</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={form.watch("startDate") || today}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category*</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No categories on this merchant. Add categories when
                          creating the merchant.
                        </div>
                      ) : (
                        categories.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={String(category.id)}
                          >
                            {categoryLabel(category)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <MerchantOfferAddressDetailsSection
              rows={addressRows}
              onChange={setAddressRows}
            />

            <TermsAndConditionsSection lines={termsLines} onChange={setTermsLines} />

            <DialogFooter className="sm:justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700"
                disabled={submitting || categories.length === 0}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Add Offer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
